import { useAppHelpers } from '@/composables/useAppHelpers.js';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const emptyProduct = () => ({ id: null, name: '', price: 0, width: 0, length: 0, extra: '' });

const coerceProduct = (product) => {
    if (!product || !product.id) return emptyProduct();
    return {
        id: product.id,
        name: product.name || '',
        price: Number(product.price) || 0,
        width: Number(product.width) || 0,
        length: Number(product.length) || 0,
        extra: product.extra || '',
    };
};

const normalizeRoom = (room) => {
    if (!room.panels) {
        room.panels = { walls: [], windows: [], doors: [], vents: [], product: emptyProduct(), panelCount: 0 };
    }
    if (!room.ceiling) {
        room.ceiling = { roof: [], product: emptyProduct(), ceilingCount: 0 };
    }
    if (!room.flooring) {
        room.flooring = { floor: [], product: emptyProduct(), flooringCount: 0 };
    }
    room.panels.product = coerceProduct(room.panels.product);
    room.ceiling.product = coerceProduct(room.ceiling.product);
    room.flooring.product = coerceProduct(room.flooring.product);
    if (!Array.isArray(room.panels.walls)) room.panels.walls = [];
    if (!Array.isArray(room.panels.windows)) room.panels.windows = [];
    if (!Array.isArray(room.panels.doors)) room.panels.doors = [];
    if (!Array.isArray(room.panels.vents)) room.panels.vents = [];
    if (!Array.isArray(room.ceiling.roof)) room.ceiling.roof = [];
    if (!Array.isArray(room.flooring.floor)) room.flooring.floor = [];
    room.panels.panelCount = Number(room.panels.panelCount) || 0;
    room.ceiling.ceilingCount = Number(room.ceiling.ceilingCount) || 0;
    room.flooring.flooringCount = Number(room.flooring.flooringCount) || 0;
    return room;
};

const loadRooms = () => {
    try {
        const raw = localStorage.getItem('calculatorData');
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map(normalizeRoom) : [];
    } catch (e) {
        return [];
    }
};

const nextId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

/**
 * Ceiling of (exact panels × safety factor). Exact count is ceiled first so
 * partial panels become whole units, then waste allowance is applied.
 */
export const panelsNeeded = (area, width, length, safetyFactor = 1.01) => {
    const productWidth = Number(width);
    const productLength = Number(length);
    const netArea = Number(area);
    const factor = Number(safetyFactor);

    if (!Number.isFinite(netArea) || netArea <= 0) return 0;
    if (!Number.isFinite(productWidth) || !Number.isFinite(productLength)) return 0;
    if (!Number.isFinite(factor) || factor <= 0) return 0;

    const productArea = productWidth * productLength;
    if (!Number.isFinite(productArea) || productArea <= 0) return 0;

    return Math.ceil(Math.ceil(netArea / productArea) * factor);
};

// ----------------------------------
// Standard calculator
// ----------------------------------

const HISTORY_STORAGE_KEY = 'calculatorHistory';
const PREFERENCES_STORAGE_KEY = 'calculatorPreferences';
const HISTORY_LIMIT = 100;

const has = (map, key) => Object.prototype.hasOwnProperty.call(map, key);

const OPERATORS = {
    '+': { precedence: 1, rightAssociative: false },
    '-': { precedence: 1, rightAssociative: false },
    '*': { precedence: 2, rightAssociative: false },
    '/': { precedence: 2, rightAssociative: false },
    '^': { precedence: 4, rightAssociative: true },
};

// Sits above × ÷ and below ^, so −2×3 is −6 while −2^2 is −4.
const UNARY_PRECEDENCE = 3;

// Square root is the one named function the pad offers; squaring rides on ^.
const FUNCTIONS = {
    sqrt: (x) => Math.sqrt(x),
};

const NUMBER_PATTERN = /^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/;
const NAME_PATTERN = /^[a-zA-Z]+/;

/**
 * Keypad glyphs and pasted figures become plain ASCII. Commas are dropped so a
 * grouped amount (1,250.75) pastes in as a number rather than as a syntax error.
 */
const normalizeExpression = (input) =>
    String(input ?? '')
        .replace(/[×·∗]/g, '*')
        .replace(/÷/g, '/')
        .replace(/[−–—]/g, '-')
        .replace(/√/g, 'sqrt')
        .replace(/,/g, '')
        .replace(/\s+/g, '');

const tokenize = (source) => {
    const tokens = [];
    let index = 0;

    const previous = () => tokens[tokens.length - 1] || null;
    const previousIsValue = () => {
        const token = previous();
        return Boolean(token) && (token.type === 'number' || token.type === 'rparen' || token.type === 'percent');
    };
    // 2(3+4) and 2pi read as multiplication, the way a written sum does.
    const pushImplicitMultiply = () => {
        if (previousIsValue()) tokens.push({ type: 'operator', value: '*' });
    };

    while (index < source.length) {
        const rest = source.slice(index);
        const char = rest.charAt(0);

        const numberMatch = NUMBER_PATTERN.exec(rest);
        if (numberMatch) {
            pushImplicitMultiply();
            tokens.push({ type: 'number', value: parseFloat(numberMatch[0]) });
            index += numberMatch[0].length;
            continue;
        }

        const nameMatch = NAME_PATTERN.exec(rest);
        if (nameMatch) {
            const name = nameMatch[0];
            const key = name.toLowerCase();
            const after = rest.charAt(name.length);
            index += name.length;

            // A lone "x" is how people type multiplication; exp( keeps its own x.
            if (key === 'x' && after !== '(') {
                tokens.push({ type: 'operator', value: '*' });
                continue;
            }
            if (has(FUNCTIONS, key)) {
                if (after !== '(') throw new Error(`Write ${key}( ) with brackets`);
                pushImplicitMultiply();
                tokens.push({ type: 'function', value: key });
                continue;
            }
            throw new Error(`Unknown name "${name}"`);
        }

        if (char === '(') {
            pushImplicitMultiply();
            tokens.push({ type: 'lparen' });
            index += 1;
            continue;
        }

        if (char === ')') {
            tokens.push({ type: 'rparen' });
            index += 1;
            continue;
        }

        if (char === '%') {
            if (!previousIsValue()) throw new Error('"%" must follow a value');
            tokens.push({ type: 'percent' });
            index += 1;
            continue;
        }

        if (has(OPERATORS, char)) {
            const token = previous();
            const isUnary =
                (char === '-' || char === '+') &&
                (!token || token.type === 'operator' || token.type === 'unary' || token.type === 'lparen' || token.type === 'function');
            tokens.push(isUnary ? { type: 'unary', value: char } : { type: 'operator', value: char });
            index += 1;
            continue;
        }

        throw new Error(`Unexpected character "${char}"`);
    }

    return tokens;
};

const toRpn = (tokens) => {
    const output = [];
    const stack = [];

    tokens.forEach((token) => {
        if (token.type === 'number' || token.type === 'percent') {
            // Percent is postfix, so in RPN it already trails the value it applies to.
            output.push(token);
            return;
        }

        if (token.type === 'function' || token.type === 'lparen') {
            stack.push(token);
            return;
        }

        if (token.type === 'unary') {
            // Right associative: never displaces what is already waiting.
            stack.push(token);
            return;
        }

        if (token.type === 'operator') {
            const current = OPERATORS[token.value];
            while (stack.length) {
                const top = stack[stack.length - 1];
                if (top.type !== 'operator' && top.type !== 'unary') break;
                const topPrecedence = top.type === 'unary' ? UNARY_PRECEDENCE : OPERATORS[top.value].precedence;
                if (topPrecedence > current.precedence || (topPrecedence === current.precedence && !current.rightAssociative)) {
                    output.push(stack.pop());
                    continue;
                }
                break;
            }
            stack.push(token);
            return;
        }

        // Closing bracket.
        let matched = false;
        while (stack.length) {
            const top = stack.pop();
            if (top.type === 'lparen') {
                matched = true;
                break;
            }
            output.push(top);
        }
        if (!matched) throw new Error('Unbalanced brackets');
        if (stack.length && stack[stack.length - 1].type === 'function') {
            output.push(stack.pop());
        }
    });

    while (stack.length) {
        const top = stack.pop();
        if (top.type === 'lparen') throw new Error('Unbalanced brackets');
        output.push(top);
    }

    return output;
};

const applyOperator = (operator, left, right) => {
    if (operator === '+') return left + right;
    if (operator === '-') return left - right;
    if (operator === '*') return left * right;
    if (operator === '/') return left / right;
    return Math.pow(left, right);
};

const evaluateRpn = (rpn) => {
    const stack = [];

    const pop = () => {
        const frame = stack.pop();
        if (!frame) throw new Error('Incomplete expression');
        return frame;
    };

    rpn.forEach((token) => {
        if (token.type === 'number') {
            stack.push({ value: token.value, percent: false });
            return;
        }

        if (token.type === 'percent') {
            const operand = pop();
            stack.push({ value: operand.value / 100, percent: true });
            return;
        }

        if (token.type === 'unary') {
            const operand = pop();
            stack.push({ value: token.value === '-' ? -operand.value : operand.value, percent: operand.percent });
            return;
        }

        if (token.type === 'function') {
            const operand = pop();
            stack.push({ value: FUNCTIONS[token.value](operand.value), percent: false });
            return;
        }

        const right = pop();
        const left = pop();
        // Shop arithmetic: 200+10% is 220, while 200×10% stays 20.
        const rightValue = right.percent && (token.value === '+' || token.value === '-') ? left.value * right.value : right.value;
        if (token.value === '/' && rightValue === 0) throw new Error('Cannot divide by zero');
        stack.push({ value: applyOperator(token.value, left.value, rightValue), percent: false });
    });

    if (stack.length !== 1) throw new Error('Incomplete expression');
    return stack[0].value;
};

/**
 * Evaluates a written expression without `eval`: everything is tokenized,
 * ordered by shunting-yard and reduced, so an unknown name is an error rather
 * than something the page runs. Throws with a readable message on bad input.
 */
export const evaluateExpression = (input) => {
    const source = normalizeExpression(input);
    if (!source) throw new Error('Nothing to calculate');

    const value = evaluateRpn(toRpn(tokenize(source)));
    if (!Number.isFinite(value)) throw new Error('Result is not a number');

    // Trims binary-float noise (0.1 + 0.2 → 0.3) well below calculator precision.
    return Number(value.toPrecision(12));
};

const loadHistory = () => {
    try {
        const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter(
                (entry) =>
                    entry &&
                    typeof entry.expression === 'string' &&
                    entry.expression.trim() !== '' &&
                    // Not Number(): a null result would coerce to a plausible 0.
                    typeof entry.result === 'number' &&
                    Number.isFinite(entry.result),
            )
            .slice(0, HISTORY_LIMIT)
            .map((entry) => ({
                id: entry.id || nextId(),
                expression: entry.expression,
                result: entry.result,
            }));
    } catch (e) {
        return [];
    }
};

const loadPreferences = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(PREFERENCES_STORAGE_KEY) || '{}');
        return { mode: parsed?.mode === 'interior' ? 'interior' : 'standard' };
    } catch (e) {
        return { mode: 'standard' };
    }
};

export const useCalculatorStore = defineStore('calculator', () => {
    const rooms = ref(loadRooms());
    const history = ref(loadHistory());
    const preferences = ref(loadPreferences());
    const safetyFactor = ref(1.01); // 1% waste / cutting allowance
    const helpers = useAppHelpers();

    const persist = () => {
        localStorage.setItem('calculatorData', JSON.stringify(rooms.value));
    };

    const persistHistory = () => {
        localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history.value));
    };

    const persistPreferences = () => {
        localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences.value));
    };

    // ----------------------------------
    // Getters (as computed properties)
    // ----------------------------------
    const getRooms = computed(() => rooms.value);

    const getHistory = computed(() => history.value);

    const calculatorMode = computed(() => preferences.value.mode);

    const totalPanels = computed(() => {
        return rooms.value.reduce((sum, room) => sum + (room.panels.panelCount || 0), 0);
    });

    const totalCeilings = computed(() => {
        return rooms.value.reduce((sum, room) => sum + (room.ceiling.ceilingCount || 0), 0);
    });

    const totalFloorings = computed(() => {
        return rooms.value.reduce((sum, room) => sum + (room.flooring.flooringCount || 0), 0);
    });

    // ----------------------------------
    // Actions
    // ----------------------------------
    const getRoomById = (id) => {
        return rooms.value.find((room) => room.id === id);
    };

    const addRoom = (roomName) => {
        const name = String(roomName || '').trim();
        if (!name) return;

        const newRoom = {
            id: nextId(),
            name,
            panels: {
                walls: [],
                windows: [],
                doors: [],
                vents: [],
                product: emptyProduct(),
                panelCount: 0,
            },
            ceiling: {
                roof: [],
                product: emptyProduct(),
                ceilingCount: 0,
            },
            flooring: {
                floor: [],
                product: emptyProduct(),
                flooringCount: 0,
            },
        };
        rooms.value.push(newRoom);
        persist();
    };

    const removeRoom = (id) => {
        rooms.value = rooms.value.filter((room) => room.id !== id);
        persist();
    };

    const addMeasurement = (roomId, category, type, measurement_1, measurement_2) => {
        const room = getRoomById(roomId);
        if (!room) return;

        if (category === 'panels') {
            if (type === 'wall') {
                room.panels.walls.push({ width: measurement_1, height: measurement_2 });
            } else if (type === 'window') {
                room.panels.windows.push({ width: measurement_1, height: measurement_2 });
            } else if (type === 'door') {
                room.panels.doors.push({ width: measurement_1, height: measurement_2 });
            } else if (type === 'vent') {
                room.panels.vents.push({ width: measurement_1, height: measurement_2 });
            }
            calculatePanelCount(roomId);
        } else if (category === 'ceiling') {
            if (type === 'roof') {
                room.ceiling.roof.push({ length: measurement_1, width: measurement_2 });
            }
            calculateCeilingCount(roomId);
        } else if (category === 'flooring') {
            if (type === 'floor') {
                room.flooring.floor.push({ length: measurement_1, width: measurement_2 });
            }
            calculateFlooringCount(roomId);
        }
        persist();
    };

    const removeMeasurement = (roomId, category, type, index) => {
        const room = getRoomById(roomId);
        if (!room) return;

        if (category === 'panels') {
            if (type === 'wall') {
                room.panels.walls.splice(index, 1);
            } else if (type === 'window') {
                room.panels.windows.splice(index, 1);
            } else if (type === 'door') {
                room.panels.doors.splice(index, 1);
            } else if (type === 'vent') {
                room.panels.vents.splice(index, 1);
            }
            calculatePanelCount(roomId);
        } else if (category === 'ceiling') {
            if (type === 'roof') {
                room.ceiling.roof.splice(index, 1);
            }
            calculateCeilingCount(roomId);
        } else if (category === 'flooring') {
            if (type === 'floor') {
                room.flooring.floor.splice(index, 1);
            }
            calculateFlooringCount(roomId);
        }
        persist();
    };

    const cloneMeasurement = (roomId, category, type, index) => {
        const room = getRoomById(roomId);
        if (!room) return;

        if (category === 'panels') {
            if (type === 'wall') {
                room.panels.walls.push({ ...room.panels.walls[index] });
            } else if (type === 'window') {
                room.panels.windows.push({ ...room.panels.windows[index] });
            } else if (type === 'door') {
                room.panels.doors.push({ ...room.panels.doors[index] });
            } else if (type === 'vent') {
                room.panels.vents.push({ ...room.panels.vents[index] });
            }
            calculatePanelCount(roomId);
        } else if (category === 'ceiling') {
            if (type === 'roof') {
                room.ceiling.roof.push({ ...room.ceiling.roof[index] });
            }
            calculateCeilingCount(roomId);
        } else if (category === 'flooring') {
            if (type === 'floor') {
                room.flooring.floor.push({ ...room.flooring.floor[index] });
            }
            calculateFlooringCount(roomId);
        }
        persist();
    };

    const updateProduct = (roomId, category, product) => {
        const room = getRoomById(roomId);
        if (!room) return;

        const next = coerceProduct(product);

        if (category === 'panels') {
            room.panels.product = next;
            calculatePanelCount(roomId);
        } else if (category === 'ceiling') {
            room.ceiling.product = next;
            calculateCeilingCount(roomId);
        } else if (category === 'flooring') {
            room.flooring.product = next;
            calculateFlooringCount(roomId);
        }
        persist();
    };

    const areaInches = (items, dimA, dimB) => {
        return items.reduce((total, item) => {
            const a = helpers.toInches(item[dimA]);
            const b = helpers.toInches(item[dimB]);
            if (typeof a !== 'number' || typeof b !== 'number') return total;
            if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return total;
            return total + a * b;
        }, 0);
    };

    const calculatePanelCount = (roomId) => {
        const room = getRoomById(roomId);
        if (!room) return;

        const wallArea = areaInches(room.panels.walls, 'width', 'height');
        const openingArea =
            areaInches(room.panels.windows, 'width', 'height') +
            areaInches(room.panels.doors, 'width', 'height') +
            areaInches(room.panels.vents, 'width', 'height');

        const netWallArea = Math.max(0, wallArea - openingArea);
        room.panels.panelCount = panelsNeeded(netWallArea, room.panels.product.width, room.panels.product.length, safetyFactor.value);
    };

    const calculateCeilingCount = (roomId) => {
        const room = getRoomById(roomId);
        if (!room) return;

        const roofArea = areaInches(room.ceiling.roof, 'length', 'width');
        room.ceiling.ceilingCount = panelsNeeded(roofArea, room.ceiling.product.width, room.ceiling.product.length, safetyFactor.value);
    };

    const calculateFlooringCount = (roomId) => {
        const room = getRoomById(roomId);
        if (!room) return;

        const floorArea = areaInches(room.flooring.floor, 'length', 'width');
        room.flooring.flooringCount = panelsNeeded(floorArea, room.flooring.product.width, room.flooring.product.length, safetyFactor.value);
    };

    const getPanelsBreakdown = (roomId) => {
        const room = getRoomById(roomId);
        if (!room) return null;

        const wallArea = areaInches(room.panels.walls, 'width', 'height');
        const openingArea =
            areaInches(room.panels.windows, 'width', 'height') +
            areaInches(room.panels.doors, 'width', 'height') +
            areaInches(room.panels.vents, 'width', 'height');
        const netArea = Math.max(0, wallArea - openingArea);
        const productArea = (Number(room.panels.product.width) || 0) * (Number(room.panels.product.length) || 0);

        return {
            wallArea,
            openingArea,
            netArea,
            productArea,
            openingsExceedWalls: openingArea > wallArea && wallArea > 0,
            hasProduct: Boolean(room.panels.product?.id) && productArea > 0,
            count: room.panels.panelCount || 0,
        };
    };

    const getSurfaceBreakdown = (roomId, category) => {
        const room = getRoomById(roomId);
        if (!room) return null;

        if (category === 'ceiling') {
            const area = areaInches(room.ceiling.roof, 'length', 'width');
            const productArea = (Number(room.ceiling.product.width) || 0) * (Number(room.ceiling.product.length) || 0);
            return {
                area,
                productArea,
                hasProduct: Boolean(room.ceiling.product?.id) && productArea > 0,
                count: room.ceiling.ceilingCount || 0,
            };
        }

        if (category === 'flooring') {
            const area = areaInches(room.flooring.floor, 'length', 'width');
            const productArea = (Number(room.flooring.product.width) || 0) * (Number(room.flooring.product.length) || 0);
            return {
                area,
                productArea,
                hasProduct: Boolean(room.flooring.product?.id) && productArea > 0,
                count: room.flooring.flooringCount || 0,
            };
        }

        return null;
    };

    const recalculateRoom = (roomId) => {
        calculatePanelCount(roomId);
        calculateCeilingCount(roomId);
        calculateFlooringCount(roomId);
        persist();
    };

    const resetCalculator = () => {
        rooms.value = [];
        localStorage.removeItem('calculatorData');
    };

    // Newest first, so the list reads top-down as the work happened.
    const addCalculation = (expression, result) => {
        const text = String(expression ?? '').trim();
        if (!text || typeof result !== 'number' || !Number.isFinite(result)) return null;

        const entry = { id: nextId(), expression: text, result };
        history.value = [entry, ...history.value].slice(0, HISTORY_LIMIT);
        persistHistory();
        return entry;
    };

    const removeCalculation = (id) => {
        history.value = history.value.filter((entry) => entry.id !== id);
        persistHistory();
    };

    const clearHistory = () => {
        history.value = [];
        localStorage.removeItem(HISTORY_STORAGE_KEY);
    };

    const setCalculatorMode = (mode) => {
        preferences.value = { ...preferences.value, mode: mode === 'interior' ? 'interior' : 'standard' };
        persistPreferences();
    };

    return {
        getRooms,
        getHistory,
        calculatorMode,
        totalPanels,
        totalCeilings,
        totalFloorings,
        safetyFactor,
        getRoomById,
        addRoom,
        removeRoom,
        addMeasurement,
        removeMeasurement,
        cloneMeasurement,
        updateProduct,
        calculatePanelCount,
        calculateCeilingCount,
        calculateFlooringCount,
        getPanelsBreakdown,
        getSurfaceBreakdown,
        recalculateRoom,
        resetCalculator,
        addCalculation,
        removeCalculation,
        clearHistory,
        setCalculatorMode,
    };
});
